import axios from 'axios';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
const API_TIMEOUT = 10000; // 10 seconds

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('helpbook_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log requests in development
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.log('🔄 API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        data: config.data
      });
    }

    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors
api.interceptors.response.use(
  (response) => {
    // Log responses in development
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.log('✅ API Response:', {
        status: response.status,
        url: response.config.url,
        data: response.data
      });
    }

    return response;
  },
  (error) => {
    console.error('❌ API Error:', error);

    // Handle specific error cases
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          // Unauthorized - clear token and redirect to login
          localStorage.removeItem('helpbook_token');
          localStorage.removeItem('helpbook_user');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          break;

        case 403:
          console.warn('🚫 Access forbidden');
          break;

        case 404:
          console.warn('🔍 Resource not found');
          break;

        case 429:
          console.warn('⏰ Rate limit exceeded');
          break;

        case 500:
          console.error('🔥 Server error');
          break;

        default:
          console.error(`❌ HTTP ${status}:`, data?.error || error.message);
      }

      // Return formatted error
      return Promise.reject({
        status,
        message: data?.error || error.message,
        data
      });
    } else if (error.request) {
      // Network error
      console.error('🌐 Network Error:', error.request);
      return Promise.reject({
        status: 0,
        message: 'Netzwerkfehler. Bitte überprüfe deine Internetverbindung.',
        data: null
      });
    } else {
      // Other error
      console.error('❌ Unknown Error:', error.message);
      return Promise.reject({
        status: -1,
        message: error.message,
        data: null
      });
    }
  }
);

// API Helper Functions
export const apiHelpers = {
  // GET request
  get: async (url, config = {}) => {
    try {
      const response = await api.get(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // POST request
  post: async (url, data = {}, config = {}) => {
    try {
      const response = await api.post(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // PUT request
  put: async (url, data = {}, config = {}) => {
    try {
      const response = await api.put(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // DELETE request
  delete: async (url, config = {}) => {
    try {
      const response = await api.delete(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Upload file
  upload: async (url, formData, onProgress = null) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: onProgress ? (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        } : undefined,
      };

      const response = await api.post(url, formData, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Auth API
export const authAPI = {
  login: (contact, password) =>
    api.post('/auth/login', { contact, password }),

  register: (userData) =>
    api.post('/auth/register', userData),

  passwordResetRequest: (contact) =>
    api.post('/auth/password-reset-request', { contact }),

  passwordResetVerify: (contact, resetCode, newPassword) =>
    api.post('/auth/password-reset-verify', { contact, resetCode, newPassword }),

  updateProfile: (profileData) =>
    api.put('/auth/profile', profileData),

  changePassword: (currentPassword, newPassword) =>
    api.put('/auth/change-password', { currentPassword, newPassword }),

  deleteAccount: () =>
    api.delete('/auth/profile'),
};

// Community API
export const communityAPI = {
  getPosts: (category = 'alle') =>
    api.get(`/community/posts?category=${category}`),

  createPost: (postData) =>
    api.post('/community/posts', postData),

  likePost: (postId) =>
    api.post(`/community/posts/${postId}/like`),
};

// Marketplace API
export const marketplaceAPI = {
  getItems: (category = 'alle', type = 'alle') =>
    api.get(`/marketplace/items?category=${category}&type=${type}`),

  createItem: (itemData) =>
    api.post('/marketplace/items', itemData),

  favoriteItem: (itemId) =>
    api.post(`/marketplace/items/${itemId}/favorite`),
};

// Friends API
export const friendsAPI = {
  getFriends: () =>
    api.get('/friends'),

  getFriendRequests: () =>
    api.get('/friends/requests'),

  sendFriendRequest: (userId) =>
    api.post('/friends/request', { userId }),

  acceptFriendRequest: (requestId) =>
    api.post(`/friends/request/${requestId}/accept`),

  declineFriendRequest: (requestId) =>
    api.post(`/friends/request/${requestId}/decline`),

  removeFriend: (friendId) =>
    api.delete(`/friends/${friendId}`),

  searchUsers: (query) =>
    api.get(`/users/search?q=${query}`),
};

// Chat API
export const chatAPI = {
  getConversations: () =>
    api.get('/chat/conversations'),

  getMessages: (partnerId) =>
    api.get(`/chat/messages/${partnerId}`),

  sendMessage: (receiverId, message) =>
    api.post('/chat/send', { receiverId, message }),

  startChat: (userId) =>
    api.post('/chat/start', { userId }),

  markAsRead: (partnerId) =>
    api.post(`/chat/read/${partnerId}`),
};

// Reviews API
export const reviewsAPI = {
  getUserReviews: (userId) =>
    api.get(`/reviews/user/${userId}`),

  createReview: (reviewData) =>
    api.post('/reviews', reviewData),
};

// Reports API
export const reportsAPI = {
  submitReport: (reportData) =>
    api.post('/reports', reportData),
};

// Notifications API
export const notificationsAPI = {
  getNotifications: () =>
    api.get('/notifications'),

  markAsRead: (id) =>
    api.post(`/notifications/${id}/read`),
};

// Feedback API
export const feedbackAPI = {
  submitFeedback: (feedbackData) =>
    api.post('/feedback', feedbackData),
};

// Blog API
export const blogAPI = {
  getArticles: () => api.get('/blog'),
  createArticle: (articleData) => api.post('/blog', articleData),
};

// Forum API
export const forumAPI = {
  getThreads: (category = 'alle') => api.get(`/forum?category=${category}`),
  getThread: (id) => api.get(`/forum/${id}`),
  createThread: (threadData) => api.post('/forum', threadData),
  createReply: (threadId, content) => api.post(`/forum/${threadId}/reply`, { content }),
};

export default api;
